import { createSlice } from '@reduxjs/toolkit';

// Helper function to get selected team from localStorage
const getStoredSelectedTeam = () => {
  try {
    const stored = localStorage.getItem('selectedTeam');
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    return null;
  }
};

const initialState = {
  isAuthenticated: !!localStorage.getItem('authToken'), // Check token immediately
  token: localStorage.getItem('authToken'),
  user: null,
  profile: null,
  loading: false,
  error: null,
  selectedTeam: getStoredSelectedTeam(),
  teams: [],
  company: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.error = null;
      
      // Only store token in localStorage for persistence
      localStorage.setItem('authToken', action.payload.token);
    },
    setProfile: (state, action) => {
      state.profile = action.payload;
      
      // Extract teams from role array
      if (action.payload.role && Array.isArray(action.payload.role)) {
        state.teams = action.payload.role.map(role => ({
          id: role.teamId,
          name: role.teamName,
          type: role.teamType === 'P' ? 'Company' : 'Team',
          accessType: role.accesstype,
          permissions: role.access,
          companyLogo: role.companyLogo
        }));
        
        // Set first team as default selected team
        if (state.teams.length > 0 && !state.selectedTeam) {
          state.selectedTeam = state.teams[0];
        }
      }
      
      // Extract company information
      if (action.payload.company) {
        state.company = action.payload.company;
      }
      
      // Also store profile in localStorage for persistence
      localStorage.setItem('userProfile', JSON.stringify(action.payload));
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.profile = null;
      state.error = action.payload;
    },
    setSelectedTeam: (state, action) => {
      state.selectedTeam = action.payload;
      // Persist selected team in localStorage
      localStorage.setItem('selectedTeam', JSON.stringify(action.payload));
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.profile = null;
      state.loading = false;
      state.error = null;
      state.selectedTeam = null;
      state.teams = [];
      state.company = null;
      
      // Clear token from localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('username');
      localStorage.removeItem('selectedTeam');
    },
    restoreSession: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.profile = action.payload.profile;
      state.error = null;
      
      // Restore teams and company from profile if available
      if (action.payload.profile?.role && Array.isArray(action.payload.profile.role)) {
        state.teams = action.payload.profile.role.map(role => ({
          id: role.teamId,
          name: role.teamName,
          type: role.teamType === 'P' ? 'Company' : 'Team',
          accessType: role.accesstype,
          permissions: role.access,
          companyLogo: role.companyLogo
        }));
        
        if (state.teams.length > 0 && !state.selectedTeam) {
          state.selectedTeam = state.teams[0];
        }
      }
      
      if (action.payload.profile?.company) {
        state.company = action.payload.profile.company;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    sessionRestoreFailure: (state) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.error = null;
    }
  }
});

export const {
  loginStart,
  loginSuccess,
  setProfile,
  loginFailure,
  logout,
  restoreSession,
  clearError,
  setSelectedTeam,
  sessionRestoreFailure
} = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectProfile = (state) => state.auth.profile;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectSelectedTeam = (state) => state.auth.selectedTeam;
export const selectTeams = (state) => state.auth.teams;
export const selectCompany = (state) => state.auth.company;
export const selectToken = (state) => state.auth.token;

export default authSlice.reducer;